<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { listImportSessions, deleteImportSession } from '@/api/api.js'
import { useToast } from '@/composables/useToast'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import ImportSessionCard from '@/components/import/ImportSessionCard.vue'
import BaseConfirmModal from '@/components/base/BaseConfirmModal.vue'
import MdiDatabaseImport from '~icons/mdi/database-import'

const router = useRouter()
const { t } = useI18n()
const toast = useToast()

const sessions = ref([])
const loading = ref(true)
const sessionToDelete = ref(null)
const deleting = ref(false)

onMounted(async () => {
  await load()
})

async function load() {
  loading.value = true
  try {
    const res = await listImportSessions()
    sessions.value = res.results || (Array.isArray(res) ? res : [])
  } catch {
    toast.error(t('import.loadError'))
  } finally {
    loading.value = false
  }
}

function newImport() {
  router.push('/import/new')
}

function confirmDelete(sessionId) {
  sessionToDelete.value = sessionId
}

async function doDelete() {
  if (!sessionToDelete.value) return
  deleting.value = true
  try {
    await deleteImportSession(sessionToDelete.value)
    sessions.value = sessions.value.filter((s) => s.id !== sessionToDelete.value)
    toast.success(t('import.deleteSuccess'))
  } catch {
    toast.error(t('import.deleteError'))
  } finally {
    deleting.value = false
    sessionToDelete.value = null
  }
}
</script>

<template>
  <div class="page-view">
    <BasePageHeader :title="$t('import.importItems')" :show-back="false">
      <template #actions>
        <button class="btn btn-primary flex items-center gap-2" @click="newImport">
          <MdiDatabaseImport class="w-4 h-4" />
          {{ $t('import.new') }}
        </button>
      </template>
    </BasePageHeader>

    <div class="p-4 flex flex-col gap-4">
      <!-- Loading -->
      <div v-if="loading" class="flex justify-center py-12">
        <span
          class="inline-block w-6 h-6 rounded-full border-2 border-(--color-accent) border-t-transparent animate-spin"
        />
      </div>

      <!-- Empty state -->
      <div
        v-else-if="sessions.length === 0"
        class="flex flex-col items-center gap-4 py-16 text-center"
      >
        <MdiDatabaseImport class="w-12 h-12 text-(--color-muted)" />
        <p class="text-(--color-text) font-medium">{{ $t('import.noImportsYet') }}</p>
        <p class="text-(--color-muted) text-sm">
          {{ $t('import.noImportsDesc') }}
        </p>
        <button class="btn btn-primary" @click="newImport">{{ $t('import.startFirstImport') }}</button>
      </div>

      <!-- Session list -->
      <div v-else class="list-stack">
        <ImportSessionCard
          v-for="session in sessions"
          :key="session.id"
          :session="session"
          @delete="confirmDelete"
        />
      </div>
    </div>

    <!-- Delete confirmation -->
    <BaseConfirmModal
      :show="!!sessionToDelete"
      :title="$t('import.deleteTitle')"
      :body="$t('import.deleteBody')"
      :confirm-label="$t('import.delete')"
      :loading="deleting"
      @confirm="doDelete"
      @cancel="sessionToDelete = null"
    />
  </div>
</template>
